import { useState } from 'react';
import { useSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Button,
  Dialog,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  DialogTitle,
  FormControl,
  DialogActions,
  DialogContent,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { ICandidateApplication } from 'src/services/candidates/candidates.types';
import { useAddCandidateDocumentToApplicationMutation } from 'src/services/candidates/candidates.service';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  candidateId: string;
  candidateDocumentId: string;
  /** The candidate's applications. The caller only opens this when there is at least one. */
  applications: ICandidateApplication[];
};

const clientName = (application: ICandidateApplication) =>
  application.job.client_company_name || application.job.client?.company_name;

/**
 * Saves a separate copy of a candidate's document onto one of their applications, so it shows on
 * that application's Documents tab. Preselects the job when the candidate is on only one.
 */
export default function CandidateDocumentJobAdder({
  open,
  onClose,
  candidateId,
  candidateDocumentId,
  applications,
}: Props) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  const [applicationId, setApplicationId] = useState<string>(applications.length === 1 ? applications[0].id : '');

  const [addToApplication, { isLoading }] = useAddCandidateDocumentToApplicationMutation();

  const handleAdd = async () => {
    try {
      await addToApplication({
        candidate_id: candidateId,
        candidate_doc_id: candidateDocumentId,
        application_id: applicationId,
      }).unwrap();
      enqueueSnackbar(t('candidates.documents.add_to_job_success'), { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar(t('candidates.documents.add_to_job_error'), { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('candidates.documents.add_to_job')}</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('candidates.documents.add_to_job_help')}
        </Typography>

        <FormControl fullWidth>
          <InputLabel id="add-to-job-label">{t('candidates.documents.add_to_job_label')}</InputLabel>
          <Select
            labelId="add-to-job-label"
            label={t('candidates.documents.add_to_job_label')}
            value={applicationId}
            onChange={(e) => setApplicationId(e.target.value)}
          >
            {applications.map((application) => (
              <MenuItem key={application.id} value={application.id}>
                <Stack>
                  <Typography variant="body2">{application.job.title}</Typography>
                  {clientName(application) && (
                    <Typography variant="caption" color="text.secondary">
                      {clientName(application)}
                    </Typography>
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <LoadingButton variant="contained" loading={isLoading} disabled={!applicationId} onClick={handleAdd}>
          {t('common.add')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
